import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Exports purchase data to a professionally formatted PDF
 * @param {Array} purchases - Array of purchase objects to export
 * @param {string} filename - Name of the PDF file to save
 */
export function exportPurchasesToPDF(purchases, filename = "purchases.pdf") {
  // Initialize PDF document in portrait orientation
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  // Document dimensions
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let currentY = 10;

  // Set document properties
  doc.setProperties({
    title: "Purchase",
    subject: "Purchase Data Export",
    creator: "Afghan Cosmos Group",
  });

  // Helper function to format currency
  const formatCurrency = (value, currency) => `${value || 0} ${currency || ""}`;

  // Function to add header to each page
  const addHeader = () => {
    const shopName = "Afghan Cosmos ";

    // Add logo with reduced size
    try {
      const logoUrl = `${process.env.REACT_APP_IMAGE_URL}images/logo.jpeg`;
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = logoUrl;
      doc.addImage(img, "JPEG", pageWidth - 25, 2, 10, 10); // Reduced logo size
    } catch (error) {
      console.error("Failed to add logo:", error);
    }

    // Add title - 15pt bold
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(51, 102, 204);
    doc.text("Purchase Report", pageWidth / 2, 9, { align: "center" });

    // Add shop name - 12pt bold
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(80, 80, 80);
    doc.text(shopName, 10, 7);

    // Add generation date - smaller text
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 10, 11);

    return 18; // Reduced header height
  };

  // Add initial header
  currentY = addHeader();

  // Process each purchase
  purchases.forEach((purchase, index) => {
    // Check if we need a new page - reduced threshold for more content
    if (currentY > pageHeight - 60) {
      doc.addPage();
      currentY = addHeader();
    }

    // Get currency information
    const currency = purchase.currency_details?.title || "";

    // Purchase summary table with reduced spacing
    autoTable(doc, {
      head: [
        [
          "ID",
          "Supplier",
          "Date",
          "Discount",
          "Total Amount ",
          "Total Paid",
          "Remained",
          "Items",
        ],
      ],
      body: [
        [
          purchase.id || "N/A",
          purchase.supplier_details?.name || "N/A",
          purchase.purchase_date || "N/A",
          `${purchase.discount || 0}`,
          formatCurrency(purchase.total_price?.[currency], currency),
          formatCurrency(purchase.total_paid?.[currency], currency),
          formatCurrency(purchase.total_remained_amount?.[currency], currency),
          purchase.purchase_items?.length || 0,
        ],
      ],
      startY: currentY,
      styles: {
        fontSize: 8, // Body text 11pt normal
        cellPadding: 2, // Reduced padding
        halign: "center",
        valign: "middle",
        lineColor: [220, 220, 220],
        lineWidth: 0.1,
      },
      headStyles: {
        fontSize: 8, // Header text 12pt bold
        fontStyle: "bold",
        fillColor: [51, 102, 204],
        textColor: [255, 255, 255],
        halign: "center",
        cellPadding: 2, // Reduced padding
      },
      alternateRowStyles: {
        fillColor: [245, 245, 250],
      },
      margin: {
        top: 1, // Reduced margin
        bottom: 1, // Reduced margin
        left: 3,
        right: 3,
      },
      tableWidth: "auto",
      didDrawPage: function (data) {
        // Add header if new page was created
        if (data.pageNumber > 1) {
          currentY = addHeader();
        }
      },
    });

    currentY = doc.lastAutoTable.finalY + 0; // Reduced space to 3px

    // Purchased items table with reduced spacing
    if (purchase.purchase_items && purchase.purchase_items.length > 0) {
      autoTable(doc, {
        head: [
          ["Product", "QTY", "Buy Price", "Sale Price", "Unit"],
        ],
        body: purchase.purchase_items.map((item) => [
          item.product_details?.name || "N/A",
          item.quantity || 0,
          formatCurrency(item.buy_price, currency),
          formatCurrency(item.sale_price, currency),
          item.unit_details?.title || "N/A",
        ]),
        startY: currentY,
        styles: {
          fontSize: 8, // Body text 11pt normal
          cellPadding: 2, // Reduced padding
          halign: "center",
          valign: "middle",
          lineColor: [220, 220, 220],
          lineWidth: 0.1,
        },
        headStyles: {
          fontSize: 8, // Header text 12pt bold
          fontStyle: "bold",
          fillColor: [220, 230, 242],
          textColor: [50, 50, 50],
          halign: "center",
          cellPadding: 2, // Reduced padding
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        },
        margin: {
          top: 1, // Reduced margin
          bottom: 1, // Reduced margin
          left: 3,
          right: 3,
        },
        tableWidth: "auto",
        didDrawPage: function (data) {
          // Add header if new page was created
          if (data.pageNumber > 1) {
            currentY = addHeader();
          }
        },
      });

      currentY = doc.lastAutoTable.finalY + 2; // Minimal space after items table
    }
  });

  // Calculate summary totals
  const summaryTotals = purchases.reduce((acc, purchase) => {
    const currency = purchase.currency_details?.title || "";
    const totalAmount = parseFloat(purchase.total_price?.[currency]) || 0;
    const totalPaid = parseFloat(purchase.total_paid?.[currency]) || 0;
    const totalRemained = parseFloat(purchase.total_remained_amount?.[currency]) || 0;
    
    return {
      totalAmount: acc.totalAmount + totalAmount,
      totalPaid: acc.totalPaid + totalPaid,
      totalRemained: acc.totalRemained + totalRemained,
      currency: currency || acc.currency
    };
  }, { totalAmount: 0, totalPaid: 0, totalRemained: 0, currency: "" });

  // Add summary table
  if (currentY > pageHeight - 40) {
    doc.addPage();
    currentY = addHeader();
  }

  autoTable(doc, {
    head: [["Summary", "Total Amount", "Total Paid", "Total Remained"]],
    body: [[
      "TOTALS",
      `${summaryTotals.totalAmount.toFixed(2)} ${summaryTotals.currency}`,
      `${summaryTotals.totalPaid.toFixed(2)} ${summaryTotals.currency}`,
      `${summaryTotals.totalRemained.toFixed(2)} ${summaryTotals.currency}`
    ]],
    startY: currentY + 5,
    styles: {
      fontSize: 9,
      cellPadding: 3,
      halign: "center",
      fontStyle: "bold",
    },
    headStyles: {
      fontSize: 10,
      fontStyle: "bold",
      fillColor: [51, 102, 204],
      textColor: [255, 255, 255],
    },
    bodyStyles: {
      fillColor: [245, 245, 245],
      fontStyle: "bold",
    },
    margin: { left: 3, right: 3 },
  });

  // Add footer to all pages
  const totalPages = doc.internal.getNumberOfPages();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Add divider above footer
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(0, pageHeight - 11, pageWidth - 0, pageHeight - 11);

    // Footer text - smaller size
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text("تجزوی هول سیل", pageWidth / 2, pageHeight - 5, {
      align: "center",
    });

    // Page numbers - smaller size
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - 15, pageHeight - 5, {
      align: "right",
    });
  }

  // Save the PDF
  doc.save(filename);
}
